import type { SyncTransport } from '@ims/data'
import { openLocalDb } from './db.ts'
import type { SqlDriver } from './driver.ts'
import { LocalRepository } from './repository.ts'
import { SyncEngine, type SyncEngineOptions } from './sync-engine.ts'

export interface LocalStoreOptions extends Omit<SyncEngineOptions, 'driver' | 'transport'> {
  driver: SqlDriver
  transport: SyncTransport
}

export interface LocalStore {
  repository: LocalRepository
  engine: SyncEngine
  /** Stops syncing and closes the database. */
  close(): Promise<void>
}

/**
 * Opens (or upgrades) the tenant's local database and wires the repository to
 * the sync engine. Call engine.start() once the user is signed in and online
 * sync should run; before that, everything still works from the local cache.
 */
export async function openLocalStore(options: LocalStoreOptions): Promise<LocalStore> {
  const now = options.now ?? (() => new Date().toISOString())
  await openLocalDb(options.driver, options.tenantId, now())
  const engine = new SyncEngine(options)
  await engine.refreshState()
  const repository = new LocalRepository({
    driver: options.driver,
    engine,
    tenantId: options.tenantId,
    installationId: options.installationId,
  })
  return {
    repository,
    engine,
    close: async () => {
      engine.stop()
      await engine.idle()
      await options.driver.close()
    },
  }
}
